import { AnyTranslationsFn, Locale } from "@/types/locale";

// Types for chart data processing
export interface MonthlyProposalData {
  month: string;
  year: number;
  count: number;
  period: string; // "YYYY-MM" format for sorting
  displayName: string;
}

export interface ChartDataPoint {
  name: string;
  count: number;
  period: string;
}

export interface EntitiesChartMetrics {
  totalProposals: number;
  timeRange: string;
  trend: "increasing" | "decreasing" | "stable";
  trendPercentage: number;
  mostActiveMonth: string;
  averagePerMonth: number;
  showChart: boolean;
}

// Constants
const MINIMUM_PROPOSALS_FOR_CHART = 3;
const MAX_GAP_MONTHS = 6;

/**
 * Extract creation timestamps from entities
 */
export function extractEntityTimestamps(entities: { createdAt?: Date }[]): Date[] {
  if (!entities?.length) return [];

  return entities
    .map((proposal) => proposal.createdAt)
    .filter((date) => date != null)
    .map((date) => new Date(date))
    .filter((date) => !isNaN(date.getTime())) // Filter invalid dates
    .sort((a, b) => a.getTime() - b.getTime());
}

/**
 * Aggregate proposals by month and year
 */
export function aggregateByMonth(timestamps: Date[], locale: Locale): MonthlyProposalData[] {
  if (!timestamps.length) return [];

  const aggregation = new Map<string, { count: number; date: Date }>();

  timestamps.forEach((date) => {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-11
    const key = `${year}-${String(month + 1).padStart(2, "0")}`;

    const existing = aggregation.get(key);
    aggregation.set(key, {
      count: (existing?.count || 0) + 1,
      date: existing?.date || new Date(year, month, 1),
    });
  });

  return Array.from(aggregation.entries())
    .map(([period, { count, date }]) => {
      const [year] = period.split("-");
      return {
        month: date.toLocaleDateString(locale, { month: "short" }),
        year: parseInt(year),
        count,
        period,
        displayName: `${date.toLocaleDateString(locale, { month: "short" })} ${year}`,
      };
    })
    .sort((a, b) => a.period.localeCompare(b.period));
}

/**
 * Fill gaps in data if they are not too large
 */
export function fillGapsIfNeeded(
  monthlyData: MonthlyProposalData[],
  locale: Locale,
): MonthlyProposalData[] {
  if (monthlyData.length < 2) return monthlyData;

  const result: MonthlyProposalData[] = [];
  const sortedData = [...monthlyData].sort((a, b) => a.period.localeCompare(b.period));

  for (let i = 0; i < sortedData.length; i++) {
    result.push(sortedData[i]);

    // Check if we need to fill gaps to the next data point
    if (i < sortedData.length - 1) {
      const current = new Date(
        sortedData[i].year,
        parseInt(sortedData[i].period.split("-")[1]) - 1,
      );
      const next = new Date(
        sortedData[i + 1].year,
        parseInt(sortedData[i + 1].period.split("-")[1]) - 1,
      );

      const monthsGap = getMonthsDifference(current, next) - 1;

      // Fill gaps only if they're not too large
      if (monthsGap > 0 && monthsGap <= MAX_GAP_MONTHS) {
        for (let j = 1; j <= monthsGap; j++) {
          const gapDate = new Date(current.getFullYear(), current.getMonth() + j);
          const gapPeriod = `${gapDate.getFullYear()}-${String(gapDate.getMonth() + 1).padStart(2, "0")}`;

          result.push({
            month: gapDate.toLocaleDateString(locale, { month: "short" }),
            year: gapDate.getFullYear(),
            count: 0,
            period: gapPeriod,
            displayName: `${gapDate.toLocaleDateString(locale, { month: "short" })} ${gapDate.getFullYear()}`,
          });
        }
      }
    }
  }

  return result.sort((a, b) => a.period.localeCompare(b.period));
}

/**
 * Filter periods with significant data or fill gaps strategically
 */
export function filterSignificantPeriods(
  monthlyData: MonthlyProposalData[],
  locale: Locale,
): MonthlyProposalData[] {
  if (!monthlyData.length) return [];

  // First, fill reasonable gaps
  const dataWithGaps = fillGapsIfNeeded(monthlyData, locale);

  // For multi-year data, exclude years with zero proposals
  const years = new Set(dataWithGaps.map((d) => d.year));
  if (years.size > 1) {
    const yearTotals = new Map<number, number>();
    dataWithGaps.forEach((data) => {
      yearTotals.set(data.year, (yearTotals.get(data.year) || 0) + data.count);
    });

    // Filter out years with zero proposals
    const activeYears = Array.from(yearTotals.entries())
      .filter(([, total]) => total > 0)
      .map(([year]) => year);

    return dataWithGaps.filter((data) => activeYears.includes(data.year));
  }

  return dataWithGaps;
}

/**
 * Calculate trend direction and percentage
 */
export function calculateTrend(monthlyData: MonthlyProposalData[]): {
  trend: "increasing" | "decreasing" | "stable";
  percentage: number;
} {
  if (monthlyData.length < 2) {
    return { trend: "stable", percentage: 0 };
  }

  // Compare first and second halves
  const midPoint = Math.floor(monthlyData.length / 2);
  const firstHalf = monthlyData.slice(0, midPoint);
  const secondHalf = monthlyData.slice(midPoint);

  const firstHalfAvg = firstHalf.reduce((sum, d) => sum + d.count, 0) / firstHalf.length;
  const secondHalfAvg = secondHalf.reduce((sum, d) => sum + d.count, 0) / secondHalf.length;

  if (firstHalfAvg === 0 && secondHalfAvg === 0) {
    return { trend: "stable", percentage: 0 };
  }

  const change = secondHalfAvg - firstHalfAvg;
  const percentage = firstHalfAvg > 0 ? Math.abs((change / firstHalfAvg) * 100) : 100;

  if (Math.abs(change) < 0.1) {
    return { trend: "stable", percentage: 0 };
  }

  return {
    trend: change > 0 ? "increasing" : "decreasing",
    percentage: Math.round(percentage * 10) / 10,
  };
}

/**
 * Generate chart data from processed monthly data
 */
export function generateChartData(monthlyData: MonthlyProposalData[]): ChartDataPoint[] {
  return monthlyData.map((data) => ({
    name: data.displayName,
    count: data.count,
    period: data.period,
  }));
}

/**
 * Calculate comprehensive metrics for the chart
 */
export function calculateDisplayMetrics(
  entities: { createdAt?: Date }[],
  locale: Locale,
  tAny: AnyTranslationsFn,
): EntitiesChartMetrics {
  const timestamps = extractEntityTimestamps(entities);

  if (timestamps.length < MINIMUM_PROPOSALS_FOR_CHART) {
    return {
      totalProposals: timestamps.length,
      timeRange: "",
      trend: "stable",
      trendPercentage: 0,
      mostActiveMonth: "",
      averagePerMonth: 0,
      showChart: false,
    };
  }

  const monthlyData = aggregateByMonth(timestamps, locale);
  const filteredData = filterSignificantPeriods(monthlyData, locale);
  const { trend, percentage } = calculateTrend(filteredData);

  // Calculate time range
  const sortedTimestamps = [...timestamps].sort((a, b) => a.getTime() - b.getTime());
  const startDate = sortedTimestamps[0];

  // Find the most active month
  const monthTotals = new Map<string, number>();
  filteredData.forEach((data) => {
    const monthKey = data.month;
    monthTotals.set(monthKey, (monthTotals.get(monthKey) || 0) + data.count);
  });

  const mostActiveMonth =
    Array.from(monthTotals.entries()).sort(([, a], [, b]) => b - a)[0]?.[0] || "";

  // Calculate average per month
  const totalMonths = filteredData.length || 1;
  const averagePerMonth = Math.round((timestamps.length / totalMonths) * 10) / 10;

  return {
    totalProposals: timestamps.length,
    timeRange: formatTimeRange(startDate, locale, tAny),
    trend,
    trendPercentage: percentage,
    mostActiveMonth,
    averagePerMonth,
    showChart: true,
  };
}

/**
 * Helper function to calculate the month difference
 */
function getMonthsDifference(date1: Date, date2: Date): number {
  const years = date2.getFullYear() - date1.getFullYear();
  const months = date2.getMonth() - date1.getMonth();
  return years * 12 + months;
}

/**
 * Format time range for display
 */
function formatTimeRange(startDate: Date, locale: Locale, tAny: AnyTranslationsFn): string {
  const now = new Date();
  const monthsDiff = getMonthsDifference(startDate, now);

  if (monthsDiff <= 1) return tAny("timeRange.lastMonth");
  if (monthsDiff <= 3) return tAny("timeRange.last3Month");
  if (monthsDiff <= 6) return tAny("timeRange.last6Month");
  if (startDate.getFullYear() === now.getFullYear()) return tAny("timeRange.thisYear");
  if (startDate.getFullYear() === now.getFullYear() - 1) return tAny("timeRange.lastYear");

  return tAny("timeRange.since", {
    date: startDate.toLocaleDateString(locale, { month: "short", year: "numeric" }),
  });
}
